import React from 'react';
import { Button } from 'prosci-ui';
import { getAnnualPriceData, AnnualPriceData, CurrencyCode } from '../../utils/currency';
import InvoicePaymentLink from './components/InvoicePaymentLink';
import { usePlanDefinitions, useSubscriptionCopy, PricingData } from './usePlanDefinitions';

interface PlanSelectorProps {
  selectedTier: string;
  onTierChange: (tier: string) => void;
  isProsciCertified: boolean;
  pricingData: PricingData | null;
  onCheckout: (tier: string) => void;
  onCertificationRequired?: (tier: string) => void;
  onInvoiceRequest?: (tier: string) => void;
  invoicePaymentAvailable?: boolean;
  loading?: boolean;
  currentSubscription?: { tier: string };
  contactMode?: boolean;
  isGrantedConversion?: boolean;
  allowedTiers?: string[];
  trialEligible?: boolean;
  taxExemptEmail?: string;
  recommendedTier?: string;
}


export const PlanSelector: React.FC<PlanSelectorProps> = ({
  selectedTier,
  onTierChange,
  isProsciCertified,
  pricingData,
  onCheckout,
  onCertificationRequired,
  onInvoiceRequest,
  invoicePaymentAvailable = false,
  loading = false,
  currentSubscription,
  contactMode = false,
  isGrantedConversion = false,
  allowedTiers = ['A', 'B', 'C'],
  trialEligible = true,
  taxExemptEmail,
  recommendedTier
}) => {
  const copy = useSubscriptionCopy();
  const planDefinitions = usePlanDefinitions();

  const handleSelectPlan = (tier: string) => {
    // Get pricing info for this tier
    const tierPricing = pricingData?.tiers.find(t => t.tier === tier);

    // Check if this plan requires certification and user is not pre-certified
    if (tierPricing?.requires_certification && !isProsciCertified) {
      // Trigger certification modal workflow
      if (onCertificationRequired) {
        onCertificationRequired(tier);
      }
      return;
    }

    // User is certified or plan doesn't require it - proceed to checkout
    onTierChange(tier);
    onCheckout(tier);
  };

  // Helper to get formatted price data for a tier
  const getPriceData = (tier: string): AnnualPriceData | null => {
    if (!pricingData) return null;

    const tierPricing = pricingData.tiers.find(t => t.tier === tier);
    if (!tierPricing) return null;

    return getAnnualPriceData(tierPricing.amount, pricingData.currency as CurrencyCode);
  };

  // Render price with optional smaller currency suffix
  const renderPrice = (tier: string): React.ReactNode => {
    const priceData = getPriceData(tier);
    if (!priceData) return copy?.loading || 'Loading...';

    return (
      <>
        {priceData.price}
        {priceData.currencySuffix && (
          <span className="plan-card__currency-suffix">{priceData.currencySuffix}</span>
        )}
        {taxExemptEmail && <span className="plan-card__tax-asterisk">*</span>}
      </>
    );
  };

  return (
    <div className="plan-selector">

      {/* Plan Cards */}
      <div className="plan-selector__cards">
        {planDefinitions.map((plan) => {
          const isSelected = selectedTier === plan.tier;
          const isRecommended = recommendedTier ? plan.tier === recommendedTier : plan.tier === 'B';
          const isCurrentPlan = currentSubscription && currentSubscription.tier === plan.tier;
          const isTierAllowed = allowedTiers.includes(plan.tier);
          const isDisabled = isGrantedConversion && !isTierAllowed;

          return (
            <div key={plan.tier} className={`plan-card ${isSelected && !isRecommended ? 'plan-card--selected' : ''} ${isRecommended ? 'plan-card--recommended' : ''} ${isDisabled ? 'plan-card--disabled' : ''}`}>
              {isRecommended && (
                <div className="plan-card__recommended-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Recommended for you
                </div>
              )}
              <div className="plan-card__header">
                <span className="plan-card__label">{plan.name.toUpperCase()}</span>
                <div className="plan-card__pricing">
                  <span className="plan-card__price text-3xl md:text-4xl lg:text-5xl">{renderPrice(plan.tier)}</span>
                </div>
                <p className="plan-card__description">{plan.description}</p>
              
              </div>

              <div className="plan-card__body">
                <h4 className="plan-card__benefits-title">{copy?.keyBenefitsLabel || 'Key Benefits:'}</h4>
                <ul className="plan-card__benefits">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="plan-card__benefit">
                      <svg className="plan-card__benefit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span className="plan-card__benefit-text">
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="plan-card__footer">
                {isCurrentPlan ? (
                  <div className="plan-card__current-badge">{copy?.currentPlanBadge || 'Current Plan'}</div>
                ) : contactMode ? (
                  <Button
                    asChild
                    size="xl"
                     className="my-4"
                  >
                    <a
                      href={`mailto:support@prosci.com?subject=Subscription%20Inquiry%20-%20Tier%20${plan.tier}%20(${plan.name})`}
                    >
                      {copy?.contactSupportButton || 'Contact Support'}
                    </a>
                  </Button>
                ) : isDisabled ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="xl"
                    disabled={true}
                    className="my-4 opacity-50 cursor-not-allowed"
                  >
                    {copy?.notAvailable || 'Not Available'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="default"
                    size="xl"
                    disabled={loading}
                    onClick={() => handleSelectPlan(plan.tier)}
                    className="my-4"
                  >
                    {loading && isSelected
                      ? copy?.processingButton || 'Processing...'
                      : (() => {
                          const tierPricing = pricingData?.tiers.find(t => t.tier === plan.tier);
                          if (tierPricing?.requires_certification && !isProsciCertified) {
                            return copy?.verifyCertificationButton || 'Verify Certification';
                          }
                          return copy?.selectButton || 'Select';
                        })()
                    }
                  </Button>
                )}
                {!contactMode && !isGrantedConversion && trialEligible && <p className="plan-card__trial mb-3">{copy?.trialPeriod || '7-day trial period'}</p>}
                {!contactMode && !currentSubscription && invoicePaymentAvailable && onInvoiceRequest && (
                  <InvoicePaymentLink
                    onClick={() => onInvoiceRequest(plan.tier)}
                    label={copy?.invoicePaymentLink || 'Need to pay by invoice?'}
                  />
                )}
              </div>
              {plan.eligibilityNote && <p className="text-center text-xs font-medium bg-primary-50 text-primary p-2 mt-auto">{plan.eligibilityNote}</p>}
            </div>
          )
        })}
      </div>
      {taxExemptEmail && (
        <p className="plan-selector__tax-exempt-note">
          *{copy?.taxExemptNote || 'Tax-exempt organization?'}{' '}
          {copy?.taxExemptLinkPrefix || 'Contact'}{' '}
          <a href={`mailto:${taxExemptEmail}?subject=Tax-Exempt%20Subscription%20Inquiry`}>
            {taxExemptEmail}
          </a>{' '}
          {copy?.taxExemptLinkSuffix || 'before purchasing.'}
        </p>
      )}
    </div>
  );
};

export default PlanSelector;
